import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { ArrowRight, ChartPieSlice, Users } from "@phosphor-icons/react";

export default function Results() {
  const [g, setG] = useState(null);
  useEffect(() => {
    api.get("/results/gallery").then((r) => setG(r.data)).catch(() => {});
  }, []);

  const total = g?.total || 0;

  return (
    <div data-testid="results-page" className="min-h-screen bg-exodus-bg text-exodus-text">
      <Nav />

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-36 pb-16">
        <p className="eyebrow"><span className="gold-divider" />The Public Results Board</p>
        <h1 className="font-display text-5xl lg:text-7xl mt-4 leading-[0.95]">
          <span data-testid="results-total" className="gold-text">{total.toLocaleString()}</span> prospects analyzed.
        </h1>
        <p className="text-lg text-exodus-muted mt-8 max-w-2xl leading-relaxed">
          Anonymous aggregate of every Find-My-Fit quiz on the platform. No names, no PII —
          just the math of what people actually need.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-16">
        <div className="grid lg:grid-cols-2 gap-px bg-exodus-border">
          {/* Primary fit distribution */}
          <Card
            testid="results-primary-fit"
            icon={<ChartPieSlice size={20} className="text-exodus-gold" />}
            title="Primary product fit"
            subtitle="What the algorithm matches"
            data={g?.primary_fit || {}}
          />
          {/* Top goals */}
          <Card
            testid="results-goals"
            icon={<Users size={20} className="text-exodus-gold" />}
            title="Top stated goals"
            subtitle="What people are actually trying to fix"
            data={g?.top_goals || {}}
          />
          {/* Age distribution */}
          <Card
            testid="results-ages"
            icon={<Users size={20} className="text-exodus-gold" />}
            title="Age distribution"
            subtitle="When prospects engage"
            data={g?.age_distribution || {}}
          />
          {/* Recent anonymous feed */}
          <div data-testid="results-recent-feed" className="bg-exodus-surface p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-6">
              <Users size={20} className="text-exodus-gold" />
              <div>
                <p className="font-display text-2xl">Live anonymous feed</p>
                <p className="text-xs text-exodus-muted">Most recent matches</p>
              </div>
            </div>
            <ul className="space-y-3 max-h-[420px] overflow-auto">
              {(g?.recent_anonymous || []).map((r, i) => (
                <li key={i} className="text-sm flex items-center justify-between gap-3 border-b border-exodus-border/40 pb-2">
                  <span className="text-exodus-text"><strong className="gold-text">{r.first_initial}</strong> · {r.age_bracket}</span>
                  <span className="text-xs text-exodus-muted">{r.primary_label}</span>
                </li>
              ))}
              {!(g?.recent_anonymous || []).length && (
                <li className="text-xs text-exodus-muted">Be the first to appear on this board.</li>
              )}
            </ul>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 lg:px-12 pb-32 text-center">
        <h2 className="font-display text-4xl lg:text-5xl">
          Where do <span className="gold-text italic">you</span> fit on this board?
        </h2>
        <p className="text-base text-exodus-muted mt-6 leading-relaxed">
          Eight questions. Four minutes. Anonymous in this feed forever.
        </p>
        <Link to="/quiz" data-testid="results-cta-quiz" className="btn-gold mt-10 inline-flex items-center gap-3">
          Find my fit <ArrowRight size={16} weight="bold" />
        </Link>
      </section>

      <Footer />
    </div>
  );
}

function Card({ testid, icon, title, subtitle, data }) {
  const entries = Object.entries(data || {});
  entries.sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((acc, [, v]) => acc + v, 0) || 1;
  return (
    <div data-testid={testid} className="bg-exodus-surface p-8 lg:p-10">
      <div className="flex items-center gap-3 mb-6">
        {icon}
        <div>
          <p className="font-display text-2xl">{title}</p>
          <p className="text-xs text-exodus-muted">{subtitle}</p>
        </div>
      </div>
      {entries.length === 0 ? (
        <p className="text-sm text-exodus-muted">No data yet.</p>
      ) : (
        <div className="space-y-4">
          {entries.map(([k, v]) => {
            const pct = Math.round((v / total) * 100);
            return (
              <div key={k}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="text-exodus-text">{k}</span>
                  <span className="text-exodus-muted text-xs">{pct}% · {v}</span>
                </div>
                <div className="h-1.5 bg-exodus-elevated">
                  <div className="h-full bg-exodus-gold transition-all duration-700" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
